import type { FC } from "react";
import { Button, ButtonGroupText } from "@capacit-com/lara-law-shared-ui";
import DocumentVersionStatusBadge from "@/components/document/DocumentVersionStatusBadge";
import { useWorkspaceDocumentVersionStore } from "@/stores/workspace-document-version.store";
import {
  resolveWorkspaceDocumentVersionView,
  toSelectedWorkspaceVersion,
} from "../utils/documentVersionStatus";

/**
 * Toolbar readout of which revision the viewer is showing and whether it is
 * the document's current one.
 *
 * Picking an older revision in the version-history sidebar swaps the content
 * silently, so the only way to tell used to be to reopen that sidebar and read
 * which card was highlighted (LAR-423). A non-current revision also gets a way
 * straight back to the current one.
 *
 * It is one segment of the toolbar's "Dokument" group, so it renders its
 * parts as siblings rather than wrapping them.
 */
const WorkspaceDocumentVersionIndicator: FC = () => {
  const { workspaceDocument, selectedVersion, setSelectedVersion } =
    useWorkspaceDocumentVersionStore();

  if (!workspaceDocument) return null;

  const view = resolveWorkspaceDocumentVersionView(
    workspaceDocument,
    selectedVersion
  );
  if (!view) return null;

  const showCurrentVersion = () =>
    setSelectedVersion(
      toSelectedWorkspaceVersion(workspaceDocument, view.currentRevision)
    );

  return (
    <>
      <ButtonGroupText className="h-7 min-w-0 bg-input/30 text-xs text-muted-foreground">
        <span className="truncate">
          Version {view.version} af {view.totalVersions}
        </span>
      </ButtonGroupText>
      {view.isCurrent ? (
        <DocumentVersionStatusBadge status="current" className="h-auto" />
      ) : (
        <>
          <DocumentVersionStatusBadge
            status="older"
            className="h-auto"
            // Rolling the active revision back leaves higher-numbered
            // revisions non-current too, so the wording covers both.
            label="Ikke aktuel version"
            title={`Dokumentets aktuelle version er version ${view.currentRevision.revisionVersion}`}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-7 min-w-0 shrink px-2 text-xs"
            onClick={showCurrentVersion}
            id="workspace-doc-show-current-version-btn"
          >
            <span className="truncate">Vis aktuel version</span>
          </Button>
        </>
      )}
    </>
  );
};

export default WorkspaceDocumentVersionIndicator;
